import L from 'leaflet';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import { useEffect } from 'react';
import { useMap } from 'react-leaflet';

import {
  FIELD_CLUSTER_DISABLE_AT_ZOOM,
  FIELD_CLUSTER_MAX_RADIUS_PX,
} from '@/constants/fieldMap';
import type { LatLng } from '@/utils/fieldCoordinate';

import { createLabeledFieldMapIcon, defaultFieldMapIcon } from './fieldMapIcon';

export interface FieldClusterPoint<T> {
  id: string;
  position: LatLng;
  data: T;
}

interface FieldMarkerClusterProps<T> {
  points: FieldClusterPoint<T>[];
  onSelect: (point: FieldClusterPoint<T>) => void;
  showLabels?: boolean;
  getLabel?: (point: FieldClusterPoint<T>) => string;
  selectedId?: string | null;
  disableClustering?: boolean;
}

/**
 * Imperative MarkerClusterGroup: avoids mounting thousands of React Marker nodes.
 * Clustering is active below FIELD_CLUSTER_DISABLE_AT_ZOOM; above that each field is shown.
 */
export function FieldMarkerCluster<T>({
  points,
  onSelect,
  showLabels = false,
  getLabel,
  selectedId = null,
  disableClustering = false,
}: FieldMarkerClusterProps<T>) {
  const map = useMap();

  useEffect(() => {
    const clusterGroup = L.markerClusterGroup({
      showCoverageOnHover: false,
      spiderfyOnMaxZoom: true,
      zoomToBoundsOnClick: !showLabels,
      disableClusteringAtZoom: disableClustering ? 0 : FIELD_CLUSTER_DISABLE_AT_ZOOM,
      maxClusterRadius: (zoom: number) => {
        if (disableClustering) return 0;
        // Wider buckets when zoomed out so dense regions (e.g. Kansas) collapse early.
        if (zoom <= 4) return 90;
        if (zoom <= 6) return 70;
        if (zoom <= 8) return FIELD_CLUSTER_MAX_RADIUS_PX;
        return 40;
      },
      iconCreateFunction: (cluster) => {
        const count = cluster.getChildCount();
        const size = count < 25 ? 'small' : count < 100 ? 'medium' : 'large';
        return L.divIcon({
          html: `<div><span>${count}</span></div>`,
          className: `marker-cluster marker-cluster-${size}`,
          iconSize: L.point(40, 40),
        });
      },
    });

    const selectedOffset = 10000;
    let selectedMarker: L.Marker | null = null;

    for (const point of points) {
      const isSelected = point.id === selectedId;
      const label = getLabel ? getLabel(point) : '';
      const icon = showLabels && label
        ? createLabeledFieldMapIcon(label, isSelected)
        : defaultFieldMapIcon;
      const marker = L.marker([point.position.lat, point.position.lng], {
        icon,
        // Keep the active pin above overlapping neighbors at the same zoom.
        zIndexOffset: isSelected ? selectedOffset : 0,
        riseOnHover: true,
      });
      marker.on('click', (event) => {
        L.DomEvent.stopPropagation(event);
        onSelect(point);
      });
      clusterGroup.addLayer(marker);
      if (isSelected) {
        selectedMarker = marker;
      }
    }

    map.addLayer(clusterGroup);
    // MarkerCluster can reshuffle panes; re-assert selected pin on top after mount.
    if (selectedMarker) {
      selectedMarker.setZIndexOffset(selectedOffset);
      const el = selectedMarker.getElement();
      if (el?.parentElement) {
        el.parentElement.appendChild(el);
      }
    }

    return () => {
      map.removeLayer(clusterGroup);
      clusterGroup.clearLayers();
    };
  }, [map, points, onSelect, showLabels, getLabel, selectedId, disableClustering]);

  return null;
}
